"use client";

import { Body, Display, Label } from "@cantera/design-system";
import { createContext, useContext, useEffect, useState } from "react";

const TESTIMONIALS = [
  {
    name: "Victor V.",
    field: "Electrical Engineering · UNAM",
    quote:
      "Cantera handled the visa, the flights, everything. I just had to show up and build.",
  },
  {
    name: "Jose V.",
    field: "Civil Engineering · Tec de Monterrey",
    quote:
      "Intense weeks since I started. The team is excellent and we're moving fast.",
  },
  {
    name: "Herminio R.",
    field: "Civil Engineering · Tec de Monterrey",
    quote:
      "I went from a 15-person team in Saltillo to one of the biggest job sites in LA. The scale is completely different.",
  },
];

type CarouselContextType = {
  active: number;
  visible: boolean;
  handleDotClick: (i: number) => void;
};

const TestimonialsContext = createContext<CarouselContextType | null>(null);

function useTestimonials() {
  const ctx = useContext(TestimonialsContext);
  if (!ctx) throw new Error("Must be used within Testimonials");
  return ctx;
}

export function Testimonials({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setActive((prev) => (prev + 1) % TESTIMONIALS.length);
        setVisible(true);
      }, 400);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleDotClick = (i: number) => {
    if (i === active) return;
    setVisible(false);
    setTimeout(() => {
      setActive(i);
      setVisible(true);
    }, 400);
  };

  return (
    <TestimonialsContext.Provider value={{ active, visible, handleDotClick }}>
      {children}
    </TestimonialsContext.Provider>
  );
}

export function TestimonialCards() {
  const { active, visible } = useTestimonials();

  return (
    /* All cards occupy the same grid cell so the container height is always
       the tallest card — prevents layout shift when quotes change length. */
    <div className="grid border-0">
      {TESTIMONIALS.map((t, i) => {
        const initials = t.name
          .split(" ")
          .map((w) => w[0])
          .join("");
        const isActive = i === active && visible;

        return (
          <div
            key={t.name}
            aria-hidden={i !== active}
            style={
              {
                "--neon-border":
                  "color-mix(in oklab, var(--color-marble-500) 20%, transparent)",
              } as React.CSSProperties
            }
            className={`[grid-area:1/1] w-full rounded-lg bg-deepjade-600 dark:bg-deepjade-900 border shadow-lg p-6 transition-opacity duration-400 ${
              isActive ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <span
              className="block display-lg leading-none -mb-8 text-primary"
              aria-hidden
            >
              &ldquo;
            </span>

            <Display size="xsmall" className="text-white/90">
              {t.quote}
            </Display>

            <div className="mt-4 h-px w-full bg-white/10" aria-hidden />

            <div className="mt-4 flex items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/20 label-sm text-white/80">
                {initials}
              </div>
              <div>
                {/* TODO: --neon-border is marble-500 at 20% opacity per Figma spec — visually differs from local; revisit with designer */}
                <Label size="small" as="p" className="text-white/80">
                  {t.name}
                </Label>
                <Body size="small" className="text-white/50">
                  {t.field}
                </Body>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TestimonialDots() {
  const { active, handleDotClick } = useTestimonials();

  return (
    <div className="flex gap-1">
      {TESTIMONIALS.map((_, i) => (
        <button
          key={TESTIMONIALS[i].name}
          type="button"
          onClick={() => handleDotClick(i)}
          aria-label={`Show testimonial ${i + 1}`}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i === active ? "w-6 bg-primary" : "w-1.5 bg-white/20"
          }`}
        />
      ))}
    </div>
  );
}
